import axios from "axios";
import { useMutation } from "@tanstack/react-query";

import { updateAvailability } from "@/api/me";
import { ModalAlert } from "@/components/common/modals";
import { showToast } from "@/providers";

interface Props {
  isOpen: boolean;
  isAvailable: boolean;
  onClose: () => void;
  onSuccess: (isAvailable: boolean) => void;
}

const availableKey = String(true) as "true";
const unavailableKey = String(false) as "false";

const contentByAvailability: Record<
  "true" | "false",
  {
    title: string;
    description: string;
    action: string;
    variant: "primary" | "destructive";
    successMessage: string;
  }
> = {
  [availableKey]: {
    title: "Deseja ativar a disponibilidade?",
    description: "Ao confirmar, você ficará disponível e poderá gerenciar e receber novos atendimentos imediatamente.",
    action: "Sim, ativar",
    variant: "primary",
    successMessage: "Disponibilidade ativada!",
  },
  [unavailableKey]: {
    title: "Deseja desativar a disponibilidade?",
    description:
      "Ao confirmar, você ficará em pausa e não poderá gerenciar ou receber novos atendimentos até ativar a disponibilidade novamente.",
    action: "Sim, desativar",
    variant: "destructive",
    successMessage: "Disponibilidade desativada!",
  },
};

export const ProducerAvailabilityModal = ({ isOpen, isAvailable, onClose, onSuccess }: Props): JSX.Element => {
  const content = contentByAvailability[String(isAvailable) as keyof typeof contentByAvailability];
  const mutation = useMutation({
    mutationFn: () => updateAvailability({ isAvailable }),
    onSuccess: () => {
      onSuccess(isAvailable);
      showToast({ type: "success", title: content.successMessage });
      onClose();
    },
    onError: (error) => {
      const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined;

      showToast({
        type: "error",
        title: typeof message === "string" ? message : "Não foi possível atualizar a disponibilidade.",
        content: typeof message === "string" ? undefined : "Tente novamente em alguns instantes.",
      });
    },
  });

  return (
    <ModalAlert
      isOpen={isOpen}
      onClose={mutation.isPending ? undefined : onClose}
      isOverlayCloseable={!mutation.isPending}
      title={content.title}
      description={content.description}
      actions={[
        {
          children: "Cancelar",
          variant: "secondary",
          disabled: mutation.isPending,
          onClick: onClose,
        },
        {
          type: "button",
          children: content.action,
          variant: content.variant,
          isLoading: mutation.isPending,
          onClick: () => mutation.mutate(),
        },
      ]}
    />
  );
};
